/**
 * Component: Tracking
 * Description:
 * Captures latest-touch campaign attribution and referring traffic,
 * stores structured tracking data in localStorage,
 * and populates Umbraco Forms hidden tracking fields
 * with a single readable tracking URL/value.
 *
 * Usage:
 * tracking();
 *
 * Notes:
 * - Stores latest-touch attribution
 * - Uses localStorage
 * - Storage key remains "trackedUrl"
 * - Automatically populates hidden fields with alias "trackedUrl"
 * - Captures organic, referral and direct traffic
 * - Stores a normalised source and medium for all traffic types
 * - A new tracked visit replaces the previous tracking data
 * - Internal/untracked page visits do not overwrite existing attribution
 * - The Umbraco Forms field receives a single readable value,
 *   rather than the full JSON tracking object
 */

const STORAGE_KEY = "trackedUrl";

const TRACKING_PARAMETERS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
    "fbclid",
    "msclkid"
];


/**
 * Known search engines.
 *
 * Used to determine whether an untracked referring visit
 * should be classified as organic traffic.
 */
const ORGANIC_SEARCH_ENGINES = [
    {
        match: "google.",
        source: "google"
    },
    {
        match: "bing.",
        source: "bing"
    },
    {
        match: "yahoo.",
        source: "yahoo"
    },
    {
        match: "duckduckgo.",
        source: "duckduckgo"
    },
    {
        match: "ecosia.",
        source: "ecosia"
    },
    {
        match: "baidu.",
        source: "baidu"
    },
    {
        match: "yandex.",
        source: "yandex"
    }
];


export function tracking() {

    initialiseTracking();
    initialiseForms();
    observeForms();


    /**
     * Watch for Umbraco Forms being added/re-rendered.
     */
    function observeForms() {

        const observer = new MutationObserver(() => {
            initialiseForms();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

    }


    /**
     * Capture attribution for the current visit.
     *
     * Rules:
     *
     * 1. Explicit campaign/click tracking always becomes
     *    the latest-touch attribution.
     *
     * 2. An untracked visit does not overwrite existing attribution.
     *
     * 3. If there is no existing attribution, classify the visitor
     *    as organic, referral or direct.
     */
    function initialiseTracking() {

    const params = new URLSearchParams(
        window.location.search
    );

    const hasTracking = TRACKING_PARAMETERS.some(
        (parameter) => params.has(parameter)
    );


    /*
     * Explicit campaign tracking.
     *
     * Always overwrite the previous attribution.
     */
    if (hasTracking) {

        const trackingData =
            buildCampaignTracking(
                params
            );

        saveTracking(
            trackingData
        );

        return;
    }


    /*
     * Work with the actual referrer URL.
     *
     * Do not transform or discard it here.
     */
    const referrer =
        document.referrer;


    /*
     * No referrer means this is a new direct entry.
     *
     * Replace any previous attribution with the
     * new direct visit.
     */
    if (!referrer) {

        const trackingData =
            buildUntrackedTracking();

        saveTracking(
            trackingData
        );

        return;
    }


    /*
     * We have a referrer.
     *
     * Determine whether it belongs to this website.
     */
    let isInternalReferrer = false;

    try {

        const referrerUrl =
            new URL(referrer);

        const referrerHostname =
            normaliseHostname(
                referrerUrl.hostname
            );

        const currentHostname =
            normaliseHostname(
                window.location.hostname
            );

        isInternalReferrer =
            referrerHostname ===
            currentHostname;

    } catch (error) {

        /*
         * If the referrer cannot be parsed,
         * allow buildUntrackedTracking() to
         * deal with it.
         */
        isInternalReferrer = false;

    }


    /*
     * Internal navigation.
     *
     * Preserve the attribution that originally
     * brought the visitor to the website.
     */
    if (isInternalReferrer) {

        const existingTracking =
            getTracking();

        if (existingTracking) {
            return;
        }

    }


    /*
     * External referrer, or the edge case where
     * this is internal but no attribution exists.
     *
     * buildUntrackedTracking() deliberately stores
     * document.referrer as part of the tracking object.
     */
    const trackingData =
        buildUntrackedTracking();

    saveTracking(
        trackingData
    );

}


    /**
     * Attach to Umbraco Forms.
     *
     * IMPORTANT:
     * Do not change the "trackedUrl" field alias.
     *
     * Umbraco Forms expects this specific alias and receives
     * the readable value produced by buildTrackingUrl().
     */
    function initialiseForms() {

        const trackingField = document.querySelector(
            '[data-field-alias="trackedUrl"]'
        );

        if (!trackingField) {
            return;
        }


        populateField(
            trackingField
        );

    }


    /**
     * Populate the Umbraco Forms hidden field
     * with a single readable tracking value.
     *
     * Do not replace this with JSON.
     */
    function populateField(field) {

        const trackingUrl =
            buildTrackingUrl();

        if (!trackingUrl) {
            return;
        }


        field.value =
            trackingUrl;


        console.debug(
            "Tracking URL:",
            trackingUrl
        );

    }

}


/**
 * Build the stored attribution object for
 * explicit campaign/click tracking.
 */
function buildCampaignTracking(params) {

    const utmSource =
        params.get("utm_source");

    const utmMedium =
        params.get("utm_medium");

    const gclid =
        params.get("gclid");

    const fbclid =
        params.get("fbclid");

    const msclkid =
        params.get("msclkid");


    return {

        /*
         * Normalised attribution.
         *
         * These are the values that other JavaScript
         * integrations such as Cal.com can consume
         * consistently.
         */
        source:
            utmSource ||
            inferClickSource({
                gclid,
                fbclid,
                msclkid
            }),

        medium:
            utmMedium ||
            inferClickMedium({
                gclid,
                fbclid,
                msclkid
            }),


        /*
         * Original campaign parameters.
         */
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign:
            params.get("utm_campaign"),
        utm_term:
            params.get("utm_term"),
        utm_content:
            params.get("utm_content"),


        /*
         * Advertising click IDs.
         */
        gclid,
        fbclid,
        msclkid,


        /*
         * Visit context.
         */
        landingPage:
            window.location.href,

        referrer:
            document.referrer || null,

        visitDate:
            new Date().toISOString()

    };

}


/**
 * Build attribution for the first visit when
 * no explicit campaign parameters are present.
 */
function buildUntrackedTracking() {

    const attribution =
        classifyReferrer(
            document.referrer
        );


    return {

        /*
         * Normalised attribution.
         */
        source:
            attribution.source,

        medium:
            attribution.medium,


        /*
         * No campaign parameters were supplied.
         */
        utm_source: null,
        utm_medium: null,
        utm_campaign: null,
        utm_term: null,
        utm_content: null,


        /*
         * No advertising click IDs.
         */
        gclid: null,
        fbclid: null,
        msclkid: null,


        /*
         * Visit context.
         */
        landingPage:
            window.location.href,

        referrer:
            document.referrer || null,

        visitDate:
            new Date().toISOString()

    };

}


/**
 * Classify an untracked visitor.
 *
 * Returns:
 *
 * Google organic:
 * {
 *     source: "google",
 *     medium: "organic"
 * }
 *
 * Referral:
 * {
 *     source: "checkatrade.com",
 *     medium: "referral"
 * }
 *
 * Direct:
 * {
 *     source: "direct",
 *     medium: "none"
 * }
 */
function classifyReferrer(referrer) {

    if (!referrer) {
        return {
            source: "direct",
            medium: "none"
        };
    }

    let referrerUrl;

    try {
        referrerUrl = new URL(referrer);
    } catch (error) {
        return {
            source: "direct",
            medium: "none"
        };
    }

    const referrerHostname =
        normaliseHostname(referrerUrl.hostname);

    const currentHostname =
        normaliseHostname(window.location.hostname);


    /*
     * Internal navigation.
     *
     * Do not treat pages on the same site as referrals.
     */
    if (referrerHostname === currentHostname) {
        return {
            source: "direct",
            medium: "none"
        };
    }


    /*
     * Check for recognised search engines.
     */
    const searchEngine =
        ORGANIC_SEARCH_ENGINES.find(
            (engine) =>
                referrerHostname.includes(
                    engine.match
                )
        );

    if (searchEngine) {
        return {
            source: searchEngine.source,
            medium: "organic"
        };
    }


    /*
     * Any other external referrer
     * is treated as referral traffic.
     */
    return {
        source: referrerHostname,
        medium: "referral"
    };
}


/**
 * Infer a source from advertising click IDs
 * when utm_source has not been supplied.
 */
function inferClickSource({
    gclid,
    fbclid,
    msclkid
}) {

    if (gclid) {
        return "google";
    }

    if (fbclid) {
        return "facebook";
    }

    if (msclkid) {
        return "bing";
    }

    return null;

}


/**
 * Infer a medium from advertising click IDs
 * when utm_medium has not been supplied.
 */
function inferClickMedium({
    gclid,
    fbclid,
    msclkid
}) {

    if (
        gclid ||
        fbclid ||
        msclkid
    ) {
        return "cpc";
    }


    return null;

}


/**
 * Normalise a hostname for use as a source.
 */
function normaliseHostname(hostname) {

    return hostname
        .toLowerCase()
        .replace(/^www\./, "");

}


/**
 * Save tracking attribution.
 */
function saveTracking(trackingData) {

    try {

        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(trackingData)
        );

    } catch (error) {

        console.warn(
            "Unable to save tracking data",
            error
        );

    }

}


/**
 * Return the structured tracking object.
 *
 * This is used by the booking journey and other
 * JavaScript functionality that needs the individual
 * tracking values.
 *
 * Existing stored values created by an older version
 * of tracking.js are normalised where possible.
 */
export function getTracking() {

    let tracking;


    try {

        tracking =
            localStorage.getItem(
                STORAGE_KEY
            );

    } catch (error) {

        console.warn(
            "Unable to access tracking data",
            error
        );

        return null;

    }


    if (!tracking) {
        return null;
    }


    try {

        const trackingData =
            JSON.parse(tracking);


        /*
         * Backwards compatibility.
         *
         * Older stored tracking objects may not yet contain
         * source and medium.
         *
         * Add them dynamically so visitors who already have
         * trackedUrl in localStorage still work correctly
         * after this code is deployed.
         */
        return normaliseTracking(
            trackingData
        );

    } catch (error) {

        console.warn(
            "Unable to parse tracking data",
            error
        );


        try {

            localStorage.removeItem(
                STORAGE_KEY
            );

        } catch {
            // Ignore storage cleanup failure.
        }


        return null;

    }

}


/**
 * Add source and medium to tracking data created
 * by previous versions of this component.
 */
function normaliseTracking(trackingData) {

    if (
        trackingData.source &&
        trackingData.medium
    ) {
        return trackingData;
    }


    /*
     * Existing campaign attribution.
     */
    const inferredSource =
        trackingData.utm_source ||
        inferClickSource({
            gclid: trackingData.gclid,
            fbclid: trackingData.fbclid,
            msclkid: trackingData.msclkid
        });


    const inferredMedium =
        trackingData.utm_medium ||
        inferClickMedium({
            gclid: trackingData.gclid,
            fbclid: trackingData.fbclid,
            msclkid: trackingData.msclkid
        });


    if (
        inferredSource ||
        inferredMedium
    ) {

        return {
            ...trackingData,
            source:
                inferredSource,
            medium:
                inferredMedium
        };

    }


    /*
     * Existing organic/referral/direct attribution.
     */
    const attribution =
        classifyReferrer(
            trackingData.referrer
        );


    return {
        ...trackingData,
        source:
            attribution.source,
        medium:
            attribution.medium
    };

}


/**
 * Build the readable tracking URL/value used by
 * the Umbraco Forms hidden field.
 *
 * IMPORTANT:
 *
 * This function deliberately returns a human-readable
 * URL/value rather than the structured JSON object.
 *
 * Examples:
 *
 * Campaign:
 * https://example.com/security-cameras?utm_source=google&utm_medium=cpc&utm_campaign=home-security&gclid=ABC123
 *
 * Organic:
 * https://www.google.com/?medium=organic
 *
 * Referral:
 * https://partner.com/?medium=referral
 *
 * Direct:
 * direct
 */
export function buildTrackingUrl() {

    const tracking =
        getTracking();

    if (!tracking) {
        return null;
    }


    const params =
        new URLSearchParams();


    /*
     * Campaign parameters.
     */
    if (tracking.utm_source) {

        params.set(
            "utm_source",
            tracking.utm_source
        );

    }


    if (tracking.utm_medium) {

        params.set(
            "utm_medium",
            tracking.utm_medium
        );

    }


    if (tracking.utm_campaign) {

        params.set(
            "utm_campaign",
            tracking.utm_campaign
        );

    }


    if (tracking.utm_term) {

        params.set(
            "utm_term",
            tracking.utm_term
        );

    }


    if (tracking.utm_content) {

        params.set(
            "utm_content",
            tracking.utm_content
        );

    }


    /*
     * Advertising click IDs.
     */
    if (tracking.gclid) {

        params.set(
            "gclid",
            tracking.gclid
        );

    }


    if (tracking.fbclid) {

        params.set(
            "fbclid",
            tracking.fbclid
        );

    }


    if (tracking.msclkid) {

        params.set(
            "msclkid",
            tracking.msclkid
        );

    }


    /*
     * Campaign traffic.
     *
     * Use the original landing page as the base URL,
     * then rebuild the tracking query string to avoid
     * duplicate parameters.
     */
    if (params.toString()) {

        let landingPage =
            tracking.landingPage;


        try {

            const landingUrl =
                new URL(
                    landingPage,
                    window.location.origin
                );

            landingUrl.search = "";


            return (
                `${landingUrl.toString()}?${params.toString()}`
            );

        } catch (error) {

            const separator =
                landingPage.includes("?")
                    ? "&"
                    : "?";


            return (
                `${landingPage}${separator}${params.toString()}`
            );

        }

    }


    /*
     * Organic and referral traffic.
     *
     * IMPORTANT:
     * Preserve the existing Umbraco Forms output format.
     *
     * Organic:
     * https://www.google.com/?medium=organic
     *
     * Referral:
     * https://partner.com/?medium=referral
     */
    if (
        tracking.referrer &&
        (
            tracking.medium === "organic" ||
            tracking.medium === "referral"
        )
    ) {

        try {

            const referrerUrl =
                new URL(
                    tracking.referrer
                );


            referrerUrl.searchParams.set(
                "medium",
                tracking.medium
            );


            return (
                referrerUrl.toString()
            );

        } catch (error) {

            return (
                tracking.referrer
            );

        }

    }


    /*
     * No campaign and no referrer.
     *
     * Preserve the original Umbraco Forms value.
     */
    return "direct";

}


/**
 * Build the structured tracking payload used by
 * JavaScript integrations such as the booking journey.
 *
 * Unlike the Umbraco Forms hidden field, this function
 * deliberately returns the complete tracking object.
 */
export function buildTrackingPayload() {

    const tracking =
        getTracking();

    if (!tracking) {
        return null;
    }


    return {
        ...tracking,

        submissionPage:
            window.location.href,

        submittedAt:
            new Date().toISOString()
    };

}